import React from 'react';
import { arrayOf, func, node, oneOf, shape, string } from 'prop-types';

// Block components
import BlockCategoryCarouselItem from './BlockCategoryCarouselItem';
import BlockClientCard from './BlockClientCard';
import BlockSearchBar from './BlockSearchBar';
import BlockSectionCarouselItem from './BlockSectionCarouselItem';
import BlockHowWorkItem from './BlockHowWorkItem';
import BlockLandingButtons from './BlockLandingButtons';
import BlockListingDetail from './BlockListingDetail';
import BlockListings from './BlockListings';
import BlockStores from './BlockStores';
import BlockProcessStep from './BlockProcessStep';
import BlockDefault from './BlockDefault';
import BlockFooter from './BlockFooter';
import BlockMoneyCard from './BlockMoneyCard';
import BlockProcessStepsRows from './BlockProcessStepsRows';
import BlockSocialMediaLink from './BlockSocialMediaLink';
import BlockSubscriptionForm from './BlockSubscriptionForm';
import BlockVideoCover from './BlockVideoCover';
import BlockSubcategoryItem from './BlockSubcategoryItem';
import BlockSubcategoryImage from './BlockSubcategoryImage';
import BlockSubcategoryMenu from './BlockSubcategoryMenu';
import BlockTestimonial from './BlockTestimonial';

///////////////////////////////////////////
// Mapping of block types and components //
///////////////////////////////////////////

const defaultBlockComponents = {
  "category-carousel-item": { component: BlockCategoryCarouselItem },
  "client-card": { component: BlockClientCard },
  "how-work-item": { component: BlockHowWorkItem },
  "landing-buttons": { component: BlockLandingButtons },
  "listing-detail": { component: BlockListingDetail },
  "listings-id": { component: BlockListings },
  "stores-id": { component: BlockStores },
  "money-card": { component: BlockMoneyCard },
  "process-steps-rows": { component: BlockProcessStepsRows },
  "process-step": { component: BlockProcessStep },
  "search-bar": { component: BlockSearchBar },
  "section-carousel-item": { component: BlockSectionCarouselItem },
  "subcategory-image": { component: BlockSubcategoryImage },
  "subcategory-item": { component: BlockSubcategoryItem },
  "subcategory-menu": { component: BlockSubcategoryMenu },
  "subscription-form-block": { component: BlockSubscriptionForm },
  "testimonial": { component: BlockTestimonial },
  "video-cover": { component: BlockVideoCover },
  defaultBlock: { component: BlockDefault },
  footerBlock: { component: BlockFooter },
  socialMediaLink: { component: BlockSocialMediaLink },
};

////////////////////
// Blocks builder //
////////////////////

const BlockBuilder = props => {
  const { blocks, sectionId, options, currentUser, listings, stores, ...otherProps } = props;

  // Extract block & field component mappings from props
  // If external mapping has been included for fields
  // E.g. { h1: { component: MyAwesomeHeader } }
  const { blockComponents, fieldComponents } = options || {};
  const blockOptionsMaybe = fieldComponents ? { options: { fieldComponents } } : {};

  // If there's no block, we can't render the correct block component
  if (!blocks || blocks.length === 0) {
    return null;
  }

  // Selection of Block components
  // Combine component-mapping from props together with the default one:
  const components = { ...defaultBlockComponents, ...blockComponents };

  return (
    <>
      {blocks.map((block, index) => {
        const search = Object.keys(components).find((key) => block.blockName && block.blockName.includes(key)) || null;
        const searchId = Object.keys(components).find((key) => block.blockId && block.blockId.includes(key)) || null;
        const config = components[search] || components[searchId] || components[block.blockType];
        const Block = config?.component;
        const blockId = block.blockId || `${sectionId}-block-${index + 1}`;

        const isFooter = searchId !== null

        if (Block) {
          return (
            <Block
              blockIndex={index}
              key={`${blockId}_i${index}`}
              {...block}
              blockId={blockId}
              {...blockOptionsMaybe}
              currentUser={currentUser}
              listings={listings}
              stores={stores}
              {...otherProps}
              isFooter={isFooter}
            />
          );
        } else {
          // If the block type is unknown, the app can't know what to render
          console.warn(`Unknown block type (${block.blockType}) detected inside (${sectionId}).`);
          return null;
        }
      })}
    </>
  );
};

const propTypeBlock = shape({
  blockId: string,
  blockName: string,
  blockType: oneOf(['defaultBlock', 'footerBlock', 'socialMediaLink']).isRequired,
  // Plus all kind of unknown fields.
  // BlockBuilder doesn't really need to care about those
});

const propTypeOption = shape({
  fieldComponents: shape({ component: node, pickValidProps: func }),
  blockComponents: shape({ component: node }),
});

BlockBuilder.defaultProps = {
  blocks: [],
  options: null,
  responsiveImageSizes: null,
  className: null,
  rootClassName: null,
  mediaClassName: null,
  textClassName: null,
  ctaButtonClass: null,
};

BlockBuilder.propTypes = {
  blocks: arrayOf(propTypeBlock),
  options: propTypeOption,
  responsiveImageSizes: string,
  className: string,
  rootClassName: string,
  mediaClassName: string,
  textClassName: string,
  ctaButtonClass: string,
};

export default BlockBuilder;
